import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { adminApi, authStore } from "../../api/adminApi";
import "../module.scss";

const stamp = value => value ? new Intl.DateTimeFormat("en-SG", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "—";
const statusLabel = status => ({ waiting: "Pending", human: "Ongoing", closed: "Closed" }[status] || status || "Unknown");
const ticketCategory = ticket => String(ticket?.subject || ticket?.messages?.find(message => message.sender_type === "visitor")?.content || "").match(/^Category:\s*([^\r\n]+)/i)?.[1]?.trim() || "";
const ticketSummary = ticket => String(ticket?.subject || "").replace(/^Category:[^\r\n]*\r?\n?\s*/i, "").trim() || (ticket?.contact_mode === "email" ? "Email support request" : "Live agent request");
const notificationPermissionMessage = permission => ({
  granted: "Notifications are enabled for new tickets and live chat messages.",
  denied: "Notifications are blocked. Allow them in your browser’s site settings.",
  unsupported: "This browser does not support notifications.",
}[permission] || "");
const money = value => value == null ? "Price on request" : new Intl.NumberFormat("en-SG", { style: "currency", currency: "SGD", maximumFractionDigits: 0 }).format(value);
const safeUrl = value => { try { const url = new URL(value); return ["http:", "https:"].includes(url.protocol) ? url.href : null; } catch (_) { return null; } };

const TicketMessage = ({ message, visitorReadAt }) => {
  const listings = Array.isArray(message.metadata?.displayed_listings) ? message.metadata.displayed_listings : [];
  return <div className={`message message--${message.sender_type}`}>
    <small>{message.sender_type === "visitor" ? "Visitor" : message.sender_type === "agent" ? `${message.sender?.name || "Agent"}${message.metadata?.channel === "email" ? " · Email sent" : ""}` : "Chatbot"}</small>
    {listings.length === 0 && <p>{message.content || "Attachment"}</p>}
    {listings.length > 0 && <div className="message-listings">{listings.map((listing, index) => {
      const url = safeUrl(listing.url);
      const image = safeUrl(listing.imageUrl);
      const body = <>{image && <img src={image} alt="" loading="lazy" />}<span><strong>{[listing.make, listing.model].filter(Boolean).join(" ")}</strong><em>{[listing.year, listing.condition, listing.dealer].filter(Boolean).join(" · ")}</em><b>{money(listing.price)}</b></span></>;
      return url ? <a key={listing.id || index} className="message-listing" href={url} target="_blank" rel="noopener noreferrer">{body}</a> : <div key={listing.id || index} className="message-listing">{body}</div>;
    })}</div>}
    <time>{stamp(message.created_at)}</time>
    {message.sender_type === "agent" && message.metadata?.channel !== "email" && visitorReadAt && new Date(message.created_at) <= new Date(visitorReadAt) && <small>Seen by visitor</small>}
  </div>;
};

const TicketsPage = () => {
  const site = authStore.user().site?.key || "cloudkia";
  const [params, setParams] = useSearchParams();
  const initialTicketId = useRef(params.get("id"));
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [presence, setPresence] = useState("online");
  const [presenceLoading, setPresenceLoading] = useState(true);
  const [presenceSaving, setPresenceSaving] = useState(false);
  const [presenceMessage, setPresenceMessage] = useState("");
  const [notificationPermission, setNotificationPermission] = useState(() => typeof Notification === "undefined" ? "unsupported" : Notification.permission);
  const [notificationMessage, setNotificationMessage] = useState(() => notificationPermissionMessage(typeof Notification === "undefined" ? "unsupported" : Notification.permission));
  const knownWaiting = useRef(new Set());
  const knownLiveMessages = useRef(new Map());
  const [unreadLiveIds, setUnreadLiveIds] = useState([]);
  const initialized = useRef(false);
  const streamRef = useRef(null);
  const typingHeartbeat = useRef({ ticketId: null, sentAt: 0 });

  useEffect(() => {
    adminApi.agentAvailability(site)
      .then(result => setPresence(result.availability || "online"))
      .catch(loadError => setPresenceMessage(`Availability could not be loaded: ${loadError.message}`))
      .finally(() => setPresenceLoading(false));
  }, [site]);

  useEffect(() => {
    const syncPermission = () => {
      const permission = typeof Notification === "undefined" ? "unsupported" : Notification.permission;
      setNotificationPermission(permission);
      setNotificationMessage(notificationPermissionMessage(permission));
    };
    window.addEventListener("focus", syncPermission);
    return () => window.removeEventListener("focus", syncPermission);
  }, []);

  const loadTicket = useCallback(async (id, quiet = false) => {
    if (!quiet) setDetailLoading(true);
    try {
      const ticket = await adminApi.ticket(site, id);
      setSelected(ticket);
      if (ticket.contact_mode !== "email" && document.visibilityState === "visible" && ticket.viewed_by?.id != null && String(ticket.viewed_by.id) === String(authStore.user().id)) {
        const latestVisitor = [...(ticket.messages || [])].reverse().find(message => message.sender_type === "visitor");
        if (latestVisitor && (!ticket.agent_read_at || new Date(latestVisitor.created_at) > new Date(ticket.agent_read_at))) {
          await adminApi.readTicket(site, id, latestVisitor.id);
          setUnreadLiveIds(current => current.filter(ticketId => ticketId !== String(id)));
        }
      }
    }
    catch (loadError) { if (!quiet) setError(loadError.message); }
    finally { if (!quiet) setDetailLoading(false); }
  }, [site]);

  const refreshTickets = useCallback(async initial => {
    try {
      const data = await adminApi.tickets(site);
      const rows = data.data || [];
      const waiting = rows.filter(ticket => ticket.status === "waiting");
      const currentWaiting = new Set(waiting.map(ticket => String(ticket.id)));
      const newWaiting = waiting.filter(ticket => !knownWaiting.current.has(String(ticket.id)));
      const liveMessages = new Map(rows.filter(ticket => ticket.contact_mode !== "email" && ["waiting", "human"].includes(ticket.status)).map(ticket => [String(ticket.id), ticket.last_visitor_message_id]));
      const newLiveMessages = rows.filter(ticket => ticket.contact_mode !== "email" && ticket.status === "human" && ticket.last_visitor_message_id && knownLiveMessages.current.has(String(ticket.id)) && knownLiveMessages.current.get(String(ticket.id)) !== ticket.last_visitor_message_id && !newWaiting.some(waitingTicket => waitingTicket.id === ticket.id));
      const unreadIds = rows.filter(ticket => ticket.contact_mode !== "email" && ticket.status === "human" && ticket.last_visitor_message_at && (!ticket.agent_read_at || new Date(ticket.last_visitor_message_at) > new Date(ticket.agent_read_at))).map(ticket => String(ticket.id));
      setUnreadLiveIds(current => current.length === unreadIds.length && current.every((id, index) => id === unreadIds[index]) ? current : unreadIds);
      if (initialized.current && newWaiting.length) {
        if (typeof Audio !== "undefined") {
          const sound = new Audio("data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=");
          sound.play().catch(() => {});
        }
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          new Notification("New support ticket", { body: `${newWaiting.length} visitor${newWaiting.length === 1 ? " is" : "s are"} waiting for an agent.` });
        }
      }
      if (initialized.current && newLiveMessages.length) {
        if (typeof Notification !== "undefined" && Notification.permission === "granted") {
          newLiveMessages.forEach(ticket => new Notification("New live chat message", { body: ticket.last_visitor_message_excerpt || "A visitor sent a message.", tag: `live-chat-${ticket.id}` }));
        }
      }
      knownWaiting.current = currentWaiting;
      knownLiveMessages.current = liveMessages;
      initialized.current = true;
      setTickets(rows);
      if (initial) {
        const wanted = initialTicketId.current;
        initialTicketId.current = null;
        if (wanted) {
          const ticket = rows.find(item => String(item.id) === String(wanted));
          if (ticket && ticket.status !== "closed") {
            try { await adminApi.viewTicket(site, wanted); }
            catch (viewError) { setError(viewError.message); }
          }
          await loadTicket(wanted);
        }
      }
    } catch (loadError) { setError(loadError.message); }
    finally { if (initial) setLoading(false); }
  }, [site, loadTicket]);

  useEffect(() => {
    setLoading(true);
    refreshTickets(true);
    const timer = window.setInterval(() => refreshTickets(false), 5000);
    return () => window.clearInterval(timer);
  }, [refreshTickets]);

  useEffect(() => {
    if (!selected?.id) return undefined;
    const timer = window.setInterval(() => {
      if (!busy && document.visibilityState === "visible") void loadTicket(selected.id, true);
    }, 2500);
    const refreshVisible = () => { if (!busy && document.visibilityState === "visible") void loadTicket(selected.id, true); };
    document.addEventListener("visibilitychange", refreshVisible);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", refreshVisible); };
  }, [selected?.id, busy, loadTicket]);

  const latestMessageId = selected?.messages?.at(-1)?.id;
  useEffect(() => {
    const stream = streamRef.current;
    if (stream && latestMessageId) stream.scrollTop = stream.scrollHeight;
  }, [selected?.id, latestMessageId]);

  const openTicket = async id => {
    setUnreadLiveIds(current => current.filter(ticketId => ticketId !== String(id)));
    setParams({ id });
    setError("");
    const ticket = tickets.find(item => String(item.id) === String(id));
    if (ticket && ticket.status !== "closed") {
      try { await adminApi.viewTicket(site, id); }
      catch (loadError) { setError(loadError.message); }
    }
    await loadTicket(id);
  };

  const orderedTickets = useMemo(() => [...tickets].sort((a, b) => {
    if ((a.status === "closed") !== (b.status === "closed")) return a.status === "closed" ? 1 : -1;
    return new Date(b.last_message_at || b.updated_at || b.created_at) - new Date(a.last_message_at || a.updated_at || a.created_at);
  }), [tickets]);
  const filtered = useMemo(() => orderedTickets.filter(ticket => (!status || ticket.status === status) && `${ticket.session?.dealer_name || ""} ${ticket.session?.last_path || ""} ${ticket.id}`.toLowerCase().includes(query.toLowerCase())), [orderedTickets, query, status]);
  const waitingCount = tickets.filter(ticket => ticket.status === "waiting").length;
  const viewer = selected?.viewed_by;
  const user = authStore.user();
  const ownsTicket = viewer && [viewer.id, viewer.user_id].some(id => id != null && String(id) === String(user.id));
  const ownsTicketByEmail = viewer?.email && user.email && viewer.email.toLowerCase() === user.email.toLowerCase();
  const canWorkTicket = !viewer || ownsTicket || ownsTicketByEmail;

  const act = async (operation, clearReply = false) => {
    if (!selected) return;
    if (!canWorkTicket) { setError("This ticket is claimed by another staff member."); return; }
    setBusy(true);
    setError("");
    try {
      await operation(site, selected.id);
      if (clearReply) setReply("");
      await loadTicket(selected.id);
      await refreshTickets(false);
    } catch (actionError) { setError(actionError.message); }
    finally { setBusy(false); }
  };

  const sendReply = async event => {
    event.preventDefault();
    if (!reply.trim() || !selected) return;
    if (!canWorkTicket) { setError("This ticket is claimed by another staff member."); return; }
    const content = reply.trim();
    const ticketId = selected.id;
    const contactMode = selected.contact_mode;
    setBusy(true);
    setError("");
    try {
      const result = await (contactMode === "email" ? adminApi.emailTicket : adminApi.replyToTicket)(site, ticketId, content);
      setReply("");
      const sentAt = new Date().toISOString();
      setSelected(current => {
        if (String(current?.id) !== String(ticketId)) return current;
        return {
          ...current,
          last_message_at: sentAt,
          messages: [...(current.messages || []), {
            id: result?.messageId || `sent-${Date.now()}`,
            sender_type: "agent",
            sender: { name: authStore.user().name },
            content,
            created_at: sentAt,
            metadata: contactMode === "email" ? { channel: "email" } : {},
          }],
        };
      });
    } catch (replyError) { setError(replyError.message); }
    finally { setBusy(false); }
  };

  const updateReply = event => {
    const value = event.target.value;
    setReply(value);
    if (!value.trim() || selected?.status !== "human" || selected.contact_mode === "email") return;
    const now = Date.now();
    if (typingHeartbeat.current.ticketId === selected.id && now - typingHeartbeat.current.sentAt < 2000) return;
    typingHeartbeat.current = { ticketId: selected.id, sentAt: now };
    adminApi.ticketTyping(site, selected.id).catch(() => {});
  };

  const requestNotifications = async () => {
    if (typeof Notification === "undefined") {
      setNotificationPermission("unsupported");
      setNotificationMessage(notificationPermissionMessage("unsupported"));
      return;
    }
    if (!window.isSecureContext) {
      setNotificationMessage("Browser notifications require HTTPS or localhost.");
      return;
    }
    const permission = Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
    setNotificationPermission(permission);
    setNotificationMessage(notificationPermissionMessage(permission));
  };

  const changePresence = async event => {
    const next = event.target.value;
    const previous = presence;
    setPresence(next);
    setPresenceSaving(true);
    setPresenceMessage("");
    try {
      const result = await adminApi.setAgentAvailability(site, next);
      setPresence(result.availability || next);
      setPresenceMessage(`Availability set to ${result.availability || next}.`);
    } catch (saveError) {
      setPresence(previous);
      setPresenceMessage(`Availability could not be saved: ${saveError.message}`);
    } finally {
      setPresenceSaving(false);
    }
  };

  return <div className="module-page tickets-page">
    <div className="module-page__intro tickets-page__intro">
      <div><h2>Support tickets</h2><p>Agent requests linked to ongoing visitor conversations.</p></div>
      <div className="conversation-toolbar">
        <div className="notification-control"><button className="secondary-button" type="button" onClick={requestNotifications} disabled={["granted", "unsupported"].includes(notificationPermission)}>{notificationPermission === "granted" ? "Notifications enabled" : notificationPermission === "denied" ? "Notifications blocked" : notificationPermission === "unsupported" ? "Notifications unavailable" : "Enable notifications"}</button>{notificationMessage && notificationPermission !== "granted" && <small role="status">{notificationMessage}</small>}</div>
        <label className="presence-control">Availability<select value={presence} disabled={presenceLoading || presenceSaving} onChange={changePresence}><option value="online">Online</option><option value="away">Away</option><option value="offline">Offline</option></select></label>
      </div>
    </div>
    {presenceMessage && <div className={presenceMessage.startsWith("Availability could not") ? "notice notice--error" : "ticket-presence-status"} role="status">{presenceMessage}</div>}
    {error && <div className="notice notice--error" role="alert">{error}</div>}
    <div className="conversation-layout">
      <section className="conversation-list">
        <div className="queue-heading"><strong>Waiting queue <span>{waitingCount}</span></strong><small>Oldest first · refreshes every 5 seconds</small></div>
        <div className="search-box ticket-search-box"><span>⌕</span><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search tickets" aria-label="Search tickets" /><select className="ticket-search-status" value={status} onChange={event => setStatus(event.target.value)} aria-label="Filter ticket status"><option value="">All statuses</option><option value="waiting">Pending</option><option value="human">Ongoing</option><option value="closed">Closed</option></select></div>
        {loading ? <div className="page-loading"><p>Loading tickets…</p></div> : filtered.length ? filtered.map(ticket => (
          <article key={ticket.id} className={`ticket-conversation-row ${ticket.id === selected?.id ? "active" : ""}`}>
            <button type="button" className="ticket-conversation-row__main" onClick={() => openTicket(ticket.id)}>
              <strong className="ticket-conversation-row__title">{unreadLiveIds.includes(String(ticket.id)) && <span className="ticket-unread-dot" role="img" aria-label="Unread visitor message" title="Unread visitor message" />}{ticket.session?.dealer_name || "Website visitor"}</strong>
              <span className="ticket-conversation-row__summary"><b>{ticketSummary(ticket)}</b><span> · Ticket {String(ticket.id).slice(0, 10)}</span></span>
              <time>{stamp(ticket.last_message_at || ticket.created_at)}</time>
            </button>
            <div className="ticket-conversation-row__status-summary"><span className={`ticket-status ticket-status--${ticket.status}`}>{statusLabel(ticket.status)}</span><span className={`ticket-channel ticket-channel--${ticket.contact_mode === "email" ? "email" : "live"}`}>{ticket.contact_mode === "email" ? "Email" : "Live chat"}</span>{ticketCategory(ticket) && <span className="ticket-category">{ticketCategory(ticket)}</span>}</div>
          </article>
        )) : <div className="page-empty"><p>No tickets found.</p></div>}
      </section>
      <section className="conversation-detail">
        {selected ? <>
          <header><div><h3>{selected.session?.dealer_name || "Website visitor"}</h3><p>Ticket {String(selected.id).slice(0, 10)} · {selected.contact_mode === "email" ? "Email follow-up" : "Live chat"}</p></div><span className={`pill pill--${selected.status}`}>{statusLabel(selected.status)}</span></header>
          <div className="conversation-actions">
            {selected.status === "waiting" && <button className="primary-button" type="button" disabled={busy || !canWorkTicket} onClick={() => act(adminApi.acceptTicket)}>Accept ticket</button>}
            {selected.viewed_by && canWorkTicket && selected.status !== "closed" && <button className="secondary-button" type="button" disabled={busy} onClick={() => act(adminApi.releaseTicket)}>Release</button>}
            {selected.status !== "closed" && <button className="secondary-button" type="button" disabled={busy || !canWorkTicket} onClick={() => act(adminApi.closeTicket)}>Close ticket</button>}
            {selected.feedback && <small>Visitor feedback: {selected.feedback}{selected.feedback_comment ? ` · ${selected.feedback_comment}` : ""}</small>}
          </div>
          {selected.contact_mode === "email" && selected.contact_email && <div className="context-strip"><span>Email follow-up <strong>{selected.contact_email}</strong></span>{ticketCategory(selected) && <span>Category <strong>{ticketCategory(selected)}</strong></span>}<a href={`mailto:${selected.contact_email}?subject=${encodeURIComponent(`Support ticket ${selected.id}`)}`}>Open email app</a></div>}
          <div className="context-strip"><span>Page <strong>{selected.session?.last_path || selected.session?.landing_path || "/"}</strong></span><span>Conversation stays open after ticket closure</span><span>Created <strong>{stamp(selected.created_at)}</strong></span></div>
          <div className="message-stream" ref={streamRef}>{detailLoading ? <div className="page-loading"><p>Loading conversation…</p></div> : selected.messages?.length ? selected.messages.map(message => <TicketMessage key={message.id} message={message} visitorReadAt={selected.visitor_read_at} />) : <div className="page-empty"><p>No messages in this conversation.</p></div>}</div>
          {selected.status === "human" && <form className="conversation-reply" onSubmit={sendReply}><textarea value={reply} onChange={updateReply} placeholder={canWorkTicket ? selected.contact_mode === "email" ? `Email ${selected.contact_email}…` : "Reply to visitor…" : "This ticket is claimed by another staff member."} disabled={busy || !canWorkTicket} /><button className="primary-button" type="submit" disabled={busy || !reply.trim() || !canWorkTicket}>{selected.contact_mode === "email" ? "Send email" : "Send reply"}</button></form>}
        </> : <div className="conversation-placeholder"><span>◎</span><h3>Choose a ticket</h3><p>Ticket activity and the linked visitor conversation will appear here.</p></div>}
      </section>
    </div>
  </div>;
};

export default TicketsPage;
